import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Message } from "@arco-design/web-react";
import { clearAuthToken, getAuthToken } from "@/utils/token";

const baseURL = process.env.REACT_APP_API_BASE_URL || "https://api.yueman.vip";

axios.defaults.baseURL = baseURL;
axios.defaults.withCredentials = true;

const instance: AxiosInstance = axios.create({
  baseURL,
  timeout: 15000,
  withCredentials: true,
});

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: "Bearer " + token,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let redirectingToLogin = false;

const redirectToLogin = (message?: string) => {
  if (typeof window === "undefined" || redirectingToLogin) {
    return;
  }
  redirectingToLogin = true;
  clearAuthToken();
  window.localStorage.setItem("userStatus", "logout");
  Message.error(message || "登录信息已过期，请重新登录");
  const isLoginPage = window.location.pathname === "/login";
  if (!isLoginPage) {
    window.location.replace("/login");
  } else {
    redirectingToLogin = false;
  }
};

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response;
    if (data && typeof data === "object" && data.code === 401) {
      redirectToLogin(data.message);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      redirectToLogin(
        error.response?.data?.msg ||
          error.response?.data?.message ||
          error.message
      );
      return Promise.reject(error);
    }
    const message =
      error.response?.data?.msg ||
      error.response?.data?.message ||
      error.message ||
      "请求失败";
    Message.error(message);
    return Promise.reject(error);
  }
);

type RequestConfig<T = any> = AxiosRequestConfig<T>;

type RequestFn = {
  <T = any>(config: RequestConfig): Promise<T>;
  get<T = any>(
    url: string,
    config?: Omit<RequestConfig, "url" | "method" | "data">
  ): Promise<T>;
  post<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "url" | "method">
  ): Promise<T>;
  put<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "url" | "method">
  ): Promise<T>;
  delete<T = any>(
    url: string,
    config?: Omit<RequestConfig, "url" | "method" | "data">
  ): Promise<T>;
  instance: AxiosInstance;
};

const request = ((config: RequestConfig): Promise<any> =>
  instance.request(config).then((res) => res.data)) as RequestFn;

request.get = <T = any>(
  url: string,
  config?: Omit<RequestConfig, "url" | "method" | "data">
) =>
  request<T>({
    ...(config || {}),
    method: "get",
    url,
  });

request.post = <T = any>(
  url: string,
  data?: any,
  config?: Omit<RequestConfig, "url" | "method">
) =>
  request<T>({
    ...(config || {}),
    method: "post",
    url,
    data,
  });

request.put = <T = any>(
  url: string,
  data?: any,
  config?: Omit<RequestConfig, "url" | "method">
) =>
  request<T>({
    ...(config || {}),
    method: "put",
    url,
    data,
  });

request.delete = <T = any>(
  url: string,
  config?: Omit<RequestConfig, "url" | "method" | "data">
) =>
  request<T>({
    ...(config || {}),
    method: "delete",
    url,
  });

request.instance = instance;

export default request;
